import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import { default as OverdueBillsList } from './OverdueBillsList.react.js';
import OverdueBillDetail from './OverdueBillDetail.react.js';
import * as BrowserPermission from '../../BrowserPermission';
import * as Permission from '../../../common/util/Permission';

class OverdueBillsPage extends React.Component {
    static propTypes = {
        params: PropTypes.object,
        relay: PropTypes.object,
        user: PropTypes.object,
    };
    constructor(props) {
        super(props);
        // Read search and filter params from URL
        const urlParams = new URLSearchParams(window.location.search);
        // Default to true, but respect explicit URL parameter (for global search redirects)
        const onlyUnpaidOverdueParam = urlParams.get('onlyUnpaidOverdue');
        const onlyUnpaidOverdue = onlyUnpaidOverdueParam === 'false' ? false : true;

        this.state = {
            selected: null,
            onlyUnpaidOverdue: onlyUnpaidOverdue,
            searchQuery: urlParams.get('search') || '',
            searchGlobal: false
        };
    }

    componentDidMount() {
        try {
            const orgId = this.props.params && (this.props.params.id || this.props.params.organisationId);
            if (orgId && this.props.relay) {
                this.props.relay.setVariables({ organisationId: orgId });
            }

            // Handle ?invoice= parameter from global search
            const urlParams = new URLSearchParams(window.location.search);
            const invoiceId = urlParams.get('invoice');
            if (invoiceId) {
                // Find invoice in loaded data and select it (with delay to ensure data is loaded)
                setTimeout(() => {
                    try {
                        const org = this.props.user && this.props.user.organisation;
                        const edges = org && org.organisationOverdueBills && org.organisationOverdueBills.edges;
                        const bills = Array.isArray(edges) ? edges.map(e => e && e.node).filter(Boolean) : [];
                        const targetBill = bills.find(b => b && (b.id === invoiceId || b.realId === invoiceId));

                        if (targetBill) {
                            // Auto-fill search with invoice code to filter the list (only if not already set)
                            const currentSearch = this.state.searchQuery;
                            const billCode = targetBill.code || targetBill.variableSymbol || '';

                            if (!currentSearch && billCode) {
                                this.setState({
                                    selected: targetBill,
                                    searchQuery: billCode
                                });
                                // Update URL to include search parameter
                                const newUrl = new URL(window.location.href);
                                newUrl.searchParams.set('search', billCode);
                                window.history.replaceState({}, '', newUrl.toString());
                            } else {
                                this.setState({ selected: targetBill });
                            }

                            if (this.props.relay) {
                                this.props.relay.setVariables({ selectedBillId: invoiceId, hasSelected: true });
                            }
                        }
                    } catch (e) {}
                }, 500);
            }
        } catch (e) {}
    }

    componentDidUpdate(prevProps) {
        try {
            const prevOrg = prevProps.params && (prevProps.params.id || prevProps.params.organisationId);
            const orgId = this.props.params && (this.props.params.id || this.props.params.organisationId);
            if (orgId && orgId !== prevOrg && this.props.relay) {
                this.props.relay.setVariables({ organisationId: orgId });
            }
        } catch (e) {}
    }

    handleClose = () => {
        this.setState({ selected: null });
        try {
            if (this.props.relay) this.props.relay.setVariables({ hasSelected: false, selectedBillId: '' });
        } catch (e) {}
    };

    handleSearchChange = (query) => {
        this.setState({ searchQuery: query });
        // Update URL without reload
        const url = new URL(window.location);
        if (query) {
            url.searchParams.set('search', query);
        } else {
            url.searchParams.delete('search');
        }
        window.history.replaceState({}, '', url);
    };

    handleNext = () => {
        try {
            const org = this.props.user && this.props.user.organisation;
            const edges = org && org.organisationOverdueBills && org.organisationOverdueBills.edges;
            let allBills = Array.isArray(edges) ? edges.map(e => e && e.node).filter(Boolean) : [];

            // Apply search filter FIRST (has priority over other filters)
            const hasActiveSearch = !!(this.state.searchQuery && this.state.searchQuery.trim());
            if (hasActiveSearch) {
                const searchLower = this.state.searchQuery.trim().toLowerCase();
                allBills = allBills.filter((bill) => {
                    const code = String(bill.code || '').toLowerCase();
                    const varSym = String(bill.variableSymbol || '').toLowerCase();
                    return code.includes(searchLower) || varSym.includes(searchLower);
                });
            }

            // Apply filter: only unpaid AND overdue (but skip when search is active)
            if (this.state.onlyUnpaidOverdue && !hasActiveSearch) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                allBills = allBills.filter((bill) => {
                    const amountToPay = Number(bill.amountToPay || 0);
                    if (amountToPay <= 0) return false; // not unpaid
                    if (!bill.dueDate) return false; // no due date
                    const dueDate = new Date(bill.dueDate);
                    dueDate.setHours(0, 0, 0, 0);
                    return dueDate < today; // only if past due date
                });
            }

            // Sort by dueDate (newest first) - same logic as OverdueBillsList
            allBills.sort((a, b) => {
                const ad = a.dueDate ? new Date(a.dueDate).getTime() : 0;
                const bd = b.dueDate ? new Date(b.dueDate).getTime() : 0;
                return bd - ad;
            });
            const currentIndex = allBills.findIndex(b => b && this.state.selected && (b.id === this.state.selected.id || b.realId === this.state.selected.realId));
            if (currentIndex >= 0 && currentIndex < allBills.length - 1) {
                const nextBill = allBills[currentIndex + 1];
                this.setState({ selected: nextBill });
                const selId = nextBill && (nextBill.realId || nextBill.id);
                if (selId && this.props.relay) {
                    this.props.relay.setVariables({ selectedBillId: selId, hasSelected: true });
                }
            }
        } catch (e) {}
    };

    handlePrev = () => {
        try {
            const org = this.props.user && this.props.user.organisation;
            const edges = org && org.organisationOverdueBills && org.organisationOverdueBills.edges;
            let allBills = Array.isArray(edges) ? edges.map(e => e && e.node).filter(Boolean) : [];

            // Apply search filter FIRST (has priority over other filters)
            const hasActiveSearch = !!(this.state.searchQuery && this.state.searchQuery.trim());
            if (hasActiveSearch) {
                const searchLower = this.state.searchQuery.trim().toLowerCase();
                allBills = allBills.filter((bill) => {
                    const code = String(bill.code || '').toLowerCase();
                    const varSym = String(bill.variableSymbol || '').toLowerCase();
                    return code.includes(searchLower) || varSym.includes(searchLower);
                });
            }

            // Apply filter: only unpaid AND overdue (but skip when search is active)
            if (this.state.onlyUnpaidOverdue && !hasActiveSearch) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                allBills = allBills.filter((bill) => {
                    const amountToPay = Number(bill.amountToPay || 0);
                    if (amountToPay <= 0) return false; // not unpaid
                    if (!bill.dueDate) return false; // no due date
                    const dueDate = new Date(bill.dueDate);
                    dueDate.setHours(0, 0, 0, 0);
                    return dueDate < today; // only if past due date
                });
            }

            // Sort by dueDate (newest first) - same logic as OverdueBillsList
            allBills.sort((a, b) => {
                const ad = a.dueDate ? new Date(a.dueDate).getTime() : 0;
                const bd = b.dueDate ? new Date(b.dueDate).getTime() : 0;
                return bd - ad;
            });
            const currentIndex = allBills.findIndex(b => b && this.state.selected && (b.id === this.state.selected.id || b.realId === this.state.selected.realId));
            if (currentIndex > 0) {
                const prevBill = allBills[currentIndex - 1];
                this.setState({ selected: prevBill });
                const selId = prevBill && (prevBill.realId || prevBill.id);
                if (selId && this.props.relay) {
                    this.props.relay.setVariables({ selectedBillId: selId, hasSelected: true });
                }
            }
        } catch (e) {}
    };

    render() {
        // build selected bill with logs from Relay (organisation scope)
        let selectedBill = this.state.selected;
        const organisationId = this.props.params && (this.props.params.id || this.props.params.organisationId);
        const isDeveloper = organisationId
            ? (BrowserPermission.hasOrganisationPermission(Permission.PERM_DEVELOPER, organisationId) && BrowserPermission.developerModeEnabled(organisationId))
            : false;
        const isPartner2 = organisationId ? BrowserPermission.isPartner2(organisationId) : false;
        const isPartner1 = organisationId ? BrowserPermission.isPartner1(organisationId) : false;
        const canSeeCommunications = !!(isDeveloper || isPartner1 || isPartner2);
        try {
            const org = this.props.user && this.props.user.organisation;
            const billNode = org && org.organisationOverdueBill;
            const edges = billNode && billNode.logs && billNode.logs.edges;
            const selectedLogsAll = Array.isArray(edges) ? edges.map(e => e && e.node).filter(Boolean) : [];
            // Include log message in communications to show enhanced info (reference date, days overdue)
            const communications = selectedLogsAll
                .filter(l => l && l.communication)
                .map(l => ({ ...l.communication, logMessage: l.message }));
            const selectedLogs = selectedLogsAll.filter(l => !(l && (l.type === 'communication' || l.communication)));
            if (selectedBill && billNode) {
                // Merge billNode data (has all fields including currencyCode from GraphQL) with logs/comms
                selectedBill = { ...billNode, logs: selectedLogs, communications };
            } else if (selectedBill) {
                // Fallback: just add logs/comms to existing selectedBill
                selectedBill = { ...selectedBill, logs: selectedLogs, communications };
            }
        } catch (e) {}
        const showDetail = !!(isDeveloper || isPartner1 || isPartner2);
        const isDetailOpen = showDetail && selectedBill;
        return (
            <div>
                <style>{`
                    .invoice-slideout-backdrop {
                        position: fixed;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background: rgba(0, 0, 0, 0.5);
                        z-index: 1040;
                        opacity: 0;
                        transition: opacity 0.3s ease;
                    }
                    .invoice-slideout-backdrop.open {
                        opacity: 1;
                    }
                    .invoice-slideout-panel {
                        position: fixed;
                        right: 0;
                        top: 0;
                        width: 50%;
                        max-width: 800px;
                        min-width: 600px;
                        height: 100vh;
                        background: white;
                        box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
                        transform: translateX(100%);
                        transition: transform 0.3s ease;
                        z-index: 1050;
                        overflow-y: auto;
                    }
                    .invoice-slideout-panel.open {
                        transform: translateX(0);
                    }
                `}</style>
                <div className="row">
                    <div className="col-md-12">
                        <OverdueBillsList
                            onFilterChange={(onlyUnpaidOverdue) => {
                                this.setState({ onlyUnpaidOverdue });
                                // Update URL to reflect filter state
                                const url = new URL(window.location);
                                if (onlyUnpaidOverdue) {
                                    url.searchParams.delete('onlyUnpaidOverdue'); // Default is true, no need in URL
                                } else {
                                    url.searchParams.set('onlyUnpaidOverdue', 'false');
                                }
                                window.history.replaceState({}, '', url);
                            }}
                            onRefresh={() => {
                                try {
                                    const orgId = this.props.params && (this.props.params.id || this.props.params.organisationId);
                                    if (this.props.relay) {
                                        this.props.relay.forceFetch({ organisationId: orgId, timestamp: String(Date.now()) });
                                    }
                                } catch (e) {}
                            }}
                            onSearchChange={this.handleSearchChange}
                            onSearchGlobalChange={(searchGlobal) => {
                                this.setState({ searchGlobal });
                            }}
                            onSelect={(item) => {
                                this.setState({ selected: item });
                                try {
                                    const selId = item && (item.realId || item.id);
                                    if (selId && this.props.relay) {
                                        this.props.relay.setVariables({ selectedBillId: selId, hasSelected: true });
                                    }
                                } catch (e) {}
                            }}
                            onlyUnpaidOverdue={this.state.onlyUnpaidOverdue}
                            organisationId={this.props.params && (this.props.params.id || this.props.params.organisationId)}
                            searchGlobal={this.state.searchGlobal}
                            searchQuery={this.state.searchQuery}
                            selected={this.state.selected}
                            user={this.props.user}
                        />
                    </div>
                </div>
                {isDetailOpen ? (
                    <div>
                        <div className={`invoice-slideout-backdrop ${isDetailOpen ? 'open' : ''}`} onClick={this.handleClose}></div>
                        <div className={`invoice-slideout-panel ${isDetailOpen ? 'open' : ''}`}>
                            <OverdueBillDetail
                                bill={selectedBill}
                                canSeeCommunications={canSeeCommunications}
                                isDeveloper={isDeveloper}
                                onClear={this.handleClose}
                                onNext={this.handleNext}
                                onPrev={this.handlePrev}
                            />
                        </div>
                    </div>
                ) : null}
            </div>
        );
    }
}

export default Relay.createContainer(OverdueBillsPage, {
    initialVariables: {
        organisationId: '',
        first: 3000,
        timestamp: '',
        selectedBillId: '',
        hasSelected: false,
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                organisation(id: $organisationId) {
                    id,
                    realId,
                    organisationOverdueBills(first: $first, timestamp: $timestamp) {
                        edges { node { id, realId, code, variableSymbol, dueDate, totalAmount, amountToPay, currencyCode, isIgnored, ignoreSource, isAdvancePayment } }
                    }
                    organisationOverdueBill(id: $selectedBillId) @include(if: $hasSelected) {
                        id,
                        code,
                        amountToPay,
                        currencyCode,
                        dueDate,
                        logs(first: 20, orderBy: "timestamp desc") {
                            edges { node { id, timestamp, title, message, type, communication { id, type, timestamp, message, recipient } } }
                        }
                    }
                }
            }
        `,
    },
});


